/**
 * PRESETS DE LIGHTNING — los tipos de juego a replicar (Evolution/Pragmatic).
 *
 * Cada preset es el bloque `lightning` de una receta. Una receta lo referencia con
 * `lightning: { ...LIGHTNING_PRESETS.mega3000, activo: true }` — la ACTIVACIÓN es siempre
 * un acto explícito de la receta: todos los presets llevan `activo: false` de fábrica,
 * así una receta que olvide el override queda inerte (fail-safe estructural, no procedural).
 * Modelo: lightning-modelo-canonico.md. Datos medidos: competencia-*.md, data-scraper/*.jsonl.
 *
 * REGLA: el multiplicador REEMPLAZA el pago del pleno → el mult mínimo debe ser > pleno
 * (ser afortunado SIEMPRE paga más que un pleno normal, como el casino: pleno 19, mult mín x50).
 * `validarConfigLightning` (lightning-precook.js) exige estas invariantes al activar.
 *
 * Campos: activo, pleno (recortado, financia los mults; DEBE coincidir con el multiplicador
 *   del grupo 'directo' de la receta — el validador lo exige), tiers[{mult,peso}] (peso=frecuencia),
 *   afortunadosPorRonda{min,max,pesos}, rtpObjetivo, capPorApuesta (obligatorio; 0 = sin tope,
 *   pero tiene que estar ESCRITO), bonus{tipo,nombre,descripcionFront} (para el front).
 *
 * DOS mecánicas: (a) reemplazo-de-pleno (mega3000/lightningClasico — el tier REEMPLAZA el pago
 * del pleno) y (b) ruedaBonus (reddoor — los tiers son mults de KEY que multiplican la rueda del
 * bonus, JAMÁS pagan solos; el pago sale de bonus.ruedaBonus.tiers). El validador exige el
 * invariante correcto según la mecánica. SPEC: SPEC_lightning-bonus-reddoor.md.
 *
 * shared/ es ESM; el backend lo importa con require (Node 22).
 */

// ── MEGA ROULETTE 3000 (Pragmatic) — pocos afortunados, mults grandes ──
// Tiers = los OFICIALES documentados de la familia Lightning/Mega (Pragmatic NO publica su
// tabla propia — competencia-mecanica-exacta.md L37). Pesos RECALIBRADOS 2026-07-10 sobre
// n=2.092 cobros reales (mega3000.jsonl), redondeando valores intermedios sin tier oficial
// (ej. x250→x300, x600/x700→x500/x1000) al escalón documentado más cercano — no se inventan
// tiers nuevos con muestra chica en la cola (ver lightning-datos-duros.md §5 volatilidad).
// El mult mínimo (x50) > pleno (20): ser afortunado siempre paga más.
const mega3000 = {
    activo: false, // fail-safe: la receta activa con override explícito, nunca por spread accidental
    pleno: 20, // recortado (menos feo que el x19 del casino)
    afortunadosPorRonda: { min: 1, max: 8, pesos: [30, 40, 66, 40, 20, 10, 5, 2] }, // 1-8, media ~3
    tiers: [
        { mult: 50, peso: 28 }, // medido 28.25%
        { mult: 100, peso: 51 }, // medido 50.67%
        { mult: 150, peso: 4 }, // medido 3.54%
        { mult: 200, peso: 5 }, // medido 5.26% (absorbe x250 medido, sin tier oficial propio)
        { mult: 300, peso: 4 }, // medido 3.78%
        { mult: 500, peso: 6 }, // medido 5.64% (absorbe x400/x600/x700/x900 sin tier oficial)
        { mult: 1000, peso: 3 }, // medido 2.68% (absorbe x800/x1500 sin tier oficial)
        { mult: 2000, peso: 1 }, // medido 0.19% (n=4 — muestra chica, sostiene el peso mínimo)
        { mult: 3000, peso: 1 }, // 0 visto aún (recolector: 0/3) — mínimo hasta tener muestra
    ],
    rtpObjetivo: 0.85,
    capPorApuesta: 5000000, // cortafuegos: tope 5.000.000 centavos (= 50.000 en moneda) por apuesta. 0 = sin tope.
    // COLOCACIÓN sesgada hacia menos-pleno + GATE preventivo por fondo (PIEZA 5 y 6 del
    // lightning-spec). 0.9/0.6 validados con Monte Carlo A/B en rueda10 (lightning-datos-duros
    // §5); los umbrales de pánico son relativos al ECV → re-correr el A/B al volumen real de
    // esta rueda antes de activar (obligatorio, ver lightning-spec "Validación de la PIEZA 6").
    sesgoColocacion: { base: 1, factor: 3 },
    evitarAfortunadoEnPanico: 0.9,
    evitarAfortunadoEnProteccion: 0.6,
    bonus: {
        tipo: 'multiplicador',
        nombre: 'Mega Roulette 3000',
        descripcionFront:
            'Sectores afortunados brillan con su multiplicador (x50-x3000). Hasta 8 afortunados por ronda.',
    },
};

// ── LIGHTNING clásico (Evolution) — x50-x500, 1-5 afortunados ──
const lightningClasico = {
    activo: false, // fail-safe: la receta activa con override explícito, nunca por spread accidental
    pleno: 20, // recorte estilo casino (Evolution 29:1)
    afortunadosPorRonda: { min: 1, max: 5, pesos: [15, 40, 66, 40, 15] }, // 1-5, media 3
    tiers: [
        { mult: 50, peso: 38 },
        { mult: 100, peso: 40 },
        { mult: 200, peso: 10 },
        { mult: 300, peso: 7 },
        { mult: 400, peso: 3 },
        { mult: 500, peso: 2 },
    ],
    rtpObjetivo: 0.85,
    capPorApuesta: 5000000, // cortafuegos: tope 5.000.000 centavos (= 50.000 en moneda) por apuesta. 0 = sin tope.
    // Mismos controles preventivos que mega3000 (validados en rueda10; re-medir por receta).
    sesgoColocacion: { base: 1, factor: 3 },
    evitarAfortunadoEnPanico: 0.9,
    evitarAfortunadoEnProteccion: 0.6,
    bonus: {
        tipo: 'multiplicador',
        nombre: 'Lightning',
        descripcionFront: 'Números afortunados brillan con multiplicador (x50-x500). 1-5 por ronda.',
    },
};

// ── RED DOOR (Evolution) — keys frecuentes + bonus de puertas (SPEC_lightning-bonus-reddoor.md) ──
// MECÁNICA DISTINTA al reemplazo-de-pleno: los `tiers` de acá son mults de KEY que multiplican
// la rueda del bonus — JAMÁS pagan solos (validarConfigLightning lo prohíbe estructuralmente).
// El pago real sale de bonus.ruedaBonus.tiers (premio individual del clapper).
// CALIBRADO 2026-07-11 con tools/calibrar-reddoor-desde-datos.js sobre n=29.703 rondas medidas
// (data-scraper/reddoorroulette.jsonl): media 7.52 keys/ronda, keys x1 87.13%, E[max de 3
// clappers]=177.4 → F_individual=F_max^(1/3) (§8) → E[tierRueda]=54.5 para RTP 85%.
// Monte Carlo 500k (seed fija): RTP directo 85.94% (±1pt ✓); near-miss: x400 visible 0.041%,
// ganado 0.016% (ratio ~2.6 con 3 puertas — §7: el gigante se VE más de lo que se paga).
const reddoor = {
    activo: false, // fail-safe: la receta activa con override explícito, nunca por spread accidental
    pleno: 20, // recortado, redondo (decisión dueño; PA usa 19 — la pirámide compensa la diferencia)
    // Distribución COMPLETA medida de keys por ronda (3-15, media 7.52) — el anterior modelaba
    // solo keys con mult>1 (media ~1), ERROR corregido en la SPEC.
    afortunadosPorRonda: {
        min: 3,
        max: 15,
        pesos: [979, 2266, 3346, 4506, 5576, 4305, 2386, 3186, 924, 620, 417, 196, 996],
    },
    // Mults de la KEY (por mil, medidos): x1 dominante — la key vale por ABRIR la puerta.
    tiers: [
        { mult: 1, peso: 871 },
        { mult: 2, peso: 42 },
        { mult: 3, peso: 36 },
        { mult: 4, peso: 17 },
        { mult: 5, peso: 17 },
        { mult: 7, peso: 9 },
        { mult: 10, peso: 4 },
        { mult: 15, peso: 2 },
        { mult: 20, peso: 1 },
    ],
    rtpObjetivo: 0.85,
    capPorApuesta: 5000000, // cortafuegos: tope 5.000.000 centavos (= 50.000 en moneda) por apuesta. 0 = sin tope.
    // Gate preventivo por fondo (capa 1 — mismo mecanismo del lightning simple): en PANICO/
    // PROTECCION el resolver evita los números-key → el bonus casi no dispara con fondo flaco.
    evitarAfortunadoEnPanico: 0.9,
    evitarAfortunadoEnProteccion: 0.6,
    bonus: {
        tipo: 'ruedaBonus',
        nombre: 'Red Door',
        descripcionFront: 'La llave abre la Puerta Roja: elige tu puerta antes de que cierre el contador.',
        ruedaBonus: {
            puertas: 3, // N configurable — perilla de near-miss (más puertas = el gigante se VE más, gratis)
            ritmoGlowMs: 800, // ≥800 para que la latencia de red (50-300ms) no desfase jugador vs server
            duracionVentanaMs: 15000,
            segmentosVisuales: 64, // SOLO teatro del front — el sorteo real es por tiers, no por segmentos
            // Premio INDIVIDUAL del clapper (escalones PA x30-x400), pesos calibrados (por mil)
            // manteniendo la forma medida y clavando E[tierRueda]=54.5 para el RTP objetivo.
            tiers: [
                { mult: 30, peso: 639 },
                { mult: 40, peso: 70 },
                { mult: 50, peso: 87 },
                { mult: 60, peso: 22 },
                { mult: 80, peso: 27 },
                { mult: 100, peso: 93 },
                { mult: 150, peso: 7 },
                { mult: 200, peso: 36 },
                { mult: 300, peso: 5 },
                { mult: 400, peso: 15 },
            ],
            pesoDouble: 16, // P(double por giro) = 16/(16+1001) ≈ 1/65 — la sección DOUBLE de la rueda real
            maxDoublesEncadenados: 2, // PA (g)(6)
            capMultiplicadorFinal: 4000, // techo internacional del juego real
        },
    },
};

// ── MINI LIGHTNING (rueda de 13: 0 + 1..12) — mults CHICOS y FRECUENTES ──
// Réplica del juego de referencia del dueño (2026-08-30, captura de un gabinete digital de
// 12+0): PLENO x10 (la tabla `mini` de prod paga x12 — se recorta a x10 en la receta clon) y de
// 1 a 3 "tréboles" (afortunados) por ronda con un mult que reemplaza el pleno (x50 visible en el
// hub del juego real). La pirámide de mults del juego real NO se conoce (solo se vio el x50):
// pesos INICIALES de estudio calibrados con el simulador (`--rueda minilightning --lightning-on`).
const miniLightning = {
    activo: false, // fail-safe: la receta activa con override explícito, nunca por spread accidental
    pleno: 10, // recortado de x12 (tabla `mini`) — DEBE coincidir con el directo de la receta clon
    // 0-3 tréboles (observado en el juego de referencia), media 0.4: el trébol se VE girar seguido,
    // pero que además salga TU número con trébol es 1 cada ~33 rondas. Con pleno x10 en 13 sectores
    // el presupuesto extra es de solo 8.1 pt (base 10/13 = 76.9%) — la media es la palanca que
    // decide si entra: media 1.5 costaría 22.6 pt (imposible). Ver lightning-datos-duros.md §7.
    afortunadosPorRonda: { min: 0, max: 3, pesos: [68, 25, 6, 1] },
    tiers: [
        { mult: 20, peso: 45 }, // mínimo x20 (decisión dueño 2026-08-30) — siempre > pleno x10
        { mult: 30, peso: 25 },
        { mult: 50, peso: 22 }, // el visible en el hub del juego de referencia
        { mult: 100, peso: 8 },
    ],
    rtpObjetivo: 0.85,
    capPorApuesta: 5000000, // cortafuegos: tope 5.000.000 centavos (= 50.000 en moneda) por apuesta. 0 = sin tope.
    sesgoColocacion: { base: 1, factor: 3 },
    evitarAfortunadoEnPanico: 0.9,
    evitarAfortunadoEnProteccion: 0.6,
    bonus: {
        tipo: 'multiplicador',
        nombre: 'Mini Lightning',
        descripcionFront: 'Números afortunados brillan con multiplicador (x20-x100). 1-3 por ronda.',
    },
};

// ── MINI RED DOOR (rueda de 13, pleno x10) — 1-3 keys por ronda + puerta con mínimo garantizado ──
// Misma mecánica ruedaBonus que `reddoor` (la key JAMÁS paga sola: abre la puerta; el pago sale
// de ruedaBonus.tiers). Con pleno x10 en 13 sectores la base ya rinde 10/13 = 76.9%: quedan ~8 pt
// de presupuesto para la puerta al 85%. Fórmula §8.2 de la SPEC generalizada a N=13:
//   rtpObjetivo × N = (1−p_key)×pleno + p_key×E[valorFinal],  p_key = E[keys]/N
//   keys 1-3 con media 1.35 → p_key = 0.104 → E[valorFinal] ≈ 20 → E[tierRueda] ≈ 14-16
//   (descontando E[multKey]≈1.37 y E[dobles]≈1.03) → la puerta es casi siempre x15.
// Invariante SPEC §8.5: min(tierRueda) × min(multKey) = 15 × 1 > pleno 10. Pesos INICIALES de
// estudio: se verifican con tools/montecarlo-reddoor-integrado.js (RECETA=minireddoor).
const miniRedDoor = {
    activo: false, // fail-safe: la receta activa con override explícito, nunca por spread accidental
    pleno: 10, // recortado de x12 (tabla `mini`) — DEBE coincidir con el directo de la receta clon
    afortunadosPorRonda: { min: 1, max: 3, pesos: [70, 25, 5] }, // 1-3 keys, media ~1.35 de 13
    // Mults de la KEY (por mil): x1 dominante como en el juego real — la key vale por abrir.
    tiers: [
        { mult: 1, peso: 871 },
        { mult: 2, peso: 42 },
        { mult: 3, peso: 36 },
        { mult: 4, peso: 17 },
        { mult: 5, peso: 17 },
        { mult: 7, peso: 9 },
        { mult: 10, peso: 4 },
        { mult: 15, peso: 2 },
        { mult: 20, peso: 1 },
    ],
    rtpObjetivo: 0.85,
    capPorApuesta: 5000000, // cortafuegos: tope 5.000.000 centavos (= 50.000 en moneda) por apuesta. 0 = sin tope.
    evitarAfortunadoEnPanico: 0.9,
    evitarAfortunadoEnProteccion: 0.6,
    bonus: {
        tipo: 'ruedaBonus',
        nombre: 'Mini Red Door',
        descripcionFront: 'La llave abre la puerta: elige tu puerta antes de que cierre el contador. Mínimo x15.',
        ruedaBonus: {
            puertas: 3,
            ritmoGlowMs: 800,
            duracionVentanaMs: 15000,
            segmentosVisuales: 64,
            // Premio INDIVIDUAL de la puerta (pesos POR MIL — pesoDouble es relativo a esta suma):
            // mínimo x15 garantizado (> pleno x10), media ≈ x16 — el presupuesto que deja el
            // pleno x10 en 13 sectores es chico (ver cabecera del preset).
            tiers: [
                { mult: 15, peso: 900 },
                { mult: 20, peso: 60 },
                { mult: 30, peso: 25 },
                { mult: 50, peso: 12 },
                { mult: 100, peso: 3 },
            ],
            pesoDouble: 16, // P(double por giro) = 16/(16+1000) ≈ 1/65, igual que reddoor
            maxDoublesEncadenados: 2,
            capMultiplicadorFinal: 400, // techo del premio final (x100 × dobles encadenados)
        },
    },
};

const LIGHTNING_PRESETS = { mega3000, lightningClasico, reddoor, miniLightning, miniRedDoor };

// ESM.
export { LIGHTNING_PRESETS };
